<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-4">Fulfillment Liability</h1>
      </v-col>
    </v-row>

    <v-card class="mb-4">
      <v-card-title>
        Filters
        <v-spacer />
        <v-btn small color="primary" :loading="loading" @click="loadData">
          Refresh
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-select
              v-model="packageFilter"
              :items="packageOptions"
              label="Package"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="8">
            <div class="caption grey--text mb-2">Outstanding Package Age</div>
            <v-btn-toggle
              v-model="bucketFilter"
              mandatory
              dense
              color="primary"
            >
              <v-btn small value="all">All</v-btn>
              <v-btn small value="year1">Year 1</v-btn>
              <v-btn small value="year2">Year 2</v-btn>
              <v-btn small value="year3">Year 3</v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Current liability view -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" lg="2">
        <v-card height="100%">
          <v-card-text>
            <div class="caption grey--text">Unfulfilled</div>
            <div class="headline font-weight-bold">
              {{ selectedUnfulfilledCount.toLocaleString() }}
            </div>
            <div class="caption grey--text">{{ currentBucketLabel }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="2">
        <v-card height="100%">
          <v-card-text>
            <div class="caption grey--text">Fulfilled / Booked</div>
            <div class="headline font-weight-bold">
              {{ selectedBookedCount.toLocaleString() }}
            </div>
            <div class="caption grey--text">{{ currentBucketLabel }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="2">
        <v-card height="100%">
          <v-card-text>
            <div class="caption grey--text">Current Booked Rate</div>
            <div class="headline font-weight-bold">
              {{ formatPercent(selectedCurrentBookedRate) }}
            </div>
            <div class="caption grey--text">Current view only</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="2">
        <v-card height="100%">
          <v-card-text>
            <div class="caption grey--text">Historical Fulfillment Rate</div>
            <div class="headline font-weight-bold">
              {{ formatPercent(historicalMatureFulfillmentRate) }}
            </div>
            <div class="caption grey--text">Reserve rate</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="2">
        <v-card height="100%">
          <v-card-text>
            <div class="caption grey--text">Potential Liability</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(selectedPotentialLiability) }}
            </div>
            <div class="caption grey--text">Before fulfillment rate</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="2">
        <v-card height="100%">
          <v-card-text>
            <div class="caption grey--text">Reserve Requirement</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(selectedReserveRequirement) }}
            </div>
            <div class="caption grey--text">
              Potential x {{ formatPercent(historicalMatureFulfillmentRate) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Sales and upgrade view -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card outlined height="100%">
          <v-card-text>
            <div class="caption grey--text">Total Sales</div>
            <div class="title font-weight-bold">
              {{ formatCurrency(selectedSalesTotal) }}
            </div>
            <div class="caption grey--text">Gross package sale value</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card outlined height="100%">
          <v-card-text>
            <div class="caption grey--text">Registration Upgrades</div>
            <div class="title font-weight-bold">
              {{ formatCurrency(selectedUpgradeIncome) }}
            </div>
            <div class="caption grey--text">Paid surcharges / upgrades from MPS</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card outlined height="100%">
          <v-card-text>
            <div class="caption grey--text">Total Revenue</div>
            <div class="title font-weight-bold">
              {{ formatCurrency(selectedSalesPlusUpgrades) }}
            </div>
            <div class="caption grey--text">Sales + registration upgrades</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card outlined height="100%">
          <v-card-text>
            <div class="caption grey--text">Historical Fulfillment Cost %</div>
            <div class="title font-weight-bold">
              {{ formatPercent(selectedHistoricalCostToRevenueRate) }}
            </div>
            <div class="caption grey--text">Mature fulfillment cost / total revenue</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Aging buckets -->
    <v-row class="mb-4">
      <v-col
        v-for="bucket in bucketSummaries"
        :key="bucket.key"
        cols="12"
        md="4"
      >
        <v-card
          outlined
          height="100%"
          class="bucket-card"
          @click="bucketFilter = bucket.key"
        >
          <v-card-title class="pb-1">
            {{ bucket.label }}
            <v-spacer />
            <v-chip small outlined color="primary">
              {{ bucket.count.toLocaleString() }} unfulfilled
            </v-chip>
          </v-card-title>
          <v-card-text>
            <div class="d-flex justify-space-between mb-2">
              <span class="grey--text">Potential Liability</span>
              <strong>{{ formatCurrency(bucket.potentialLiability) }}</strong>
            </div>
            <div class="d-flex justify-space-between">
              <span class="grey--text">Reserve Requirement</span>
              <strong>{{ formatCurrency(bucket.reserveRequirement) }}</strong>
            </div>
            <div v-if="bucket.key === 'year3'" class="caption grey--text mt-3">
              A $599 third-year surcharge is deducted from expected fulfillment cost.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Package breakdown -->
    <v-card>
      <v-card-title>
        Package Breakdown
        <v-spacer />
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          dense
          style="max-width: 320px"
        />
      </v-card-title>

      <v-card-text class="pt-0 pb-2 grey--text text--darken-1">
        Main reserve uses the overall historical mature fulfillment rate. Package reserve uses
        that package's own mature rate when available. Historical fulfillment cost % compares
        mature actual fulfillment cost against original sales + registration upgrade revenue.
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="selectedPackageRows"
        :search="search"
        :loading="loading"
        :items-per-page="25"
        class="elevation-1"
        sort-by="selectedReserveOverall"
        sort-desc
      >
        <template v-slot:item.selectedPackageBookedRate="{ item }">
          {{ formatPercent(item.selectedPackageBookedRate) }}
        </template>

        <template v-slot:item.historicalPackageRate="{ item }">
          <span>{{ formatPercent(item.appliedHistoricalPackageRate) }}</span>
          <v-chip
            v-if="!item.historicalMatureIssued"
            x-small
            outlined
            class="ml-1"
          >
            Overall fallback
          </v-chip>
        </template>

        <template v-slot:item.avgActualCost="{ item }">
          <span>{{ formatCurrency(item.avgActualCost) }}</span>
          <v-chip
            v-if="item.costUsesOverallFallback"
            x-small
            outlined
            class="ml-1"
          >
            Overall fallback
          </v-chip>
        </template>

        <template v-slot:item.selectedSales="{ item }">
          {{ formatCurrency(item.selectedSales) }}
        </template>

        <template v-slot:item.selectedUpgradeIncome="{ item }">
          {{ formatCurrency(item.selectedUpgradeIncome) }}
        </template>

        <template v-slot:item.selectedSalesPlusUpgrades="{ item }">
          <strong>{{ formatCurrency(item.selectedSalesPlusUpgrades) }}</strong>
        </template>

        <template v-slot:item.historicalCostToRevenueRate="{ item }">
          <span>{{ formatPercent(item.appliedHistoricalCostToRevenueRate) }}</span>
          <v-chip
            v-if="!item.historicalMatureIssued"
            x-small
            outlined
            class="ml-1"
          >
            Overall fallback
          </v-chip>
        </template>

        <template v-slot:item.selectedPotential="{ item }">
          <strong>{{ formatCurrency(item.selectedPotential) }}</strong>
        </template>

        <template v-slot:item.selectedReserveOverall="{ item }">
          <strong>{{ formatCurrency(item.selectedReserveOverall) }}</strong>
        </template>

        <template v-slot:item.selectedReservePackage="{ item }">
          <strong>{{ formatCurrency(item.selectedReservePackage) }}</strong>
        </template>
      </v-data-table>
    </v-card>

    <!-- Validation and historical -->
    <v-expansion-panels multiple class="mt-4">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <span class="font-weight-medium">Data Validation</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Active Voided Excluded</div>
              <div class="title font-weight-bold">
                {{ voidedExcludedCount.toLocaleString() }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Invalid / Missing Issue Dates</div>
              <div class="title font-weight-bold">
                {{ (invalidIssueDateCount + missingIssueDateCount).toLocaleString() }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Future Issue Dates</div>
              <div class="title font-weight-bold">
                {{ futureIssueDateCount.toLocaleString() }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Packages Using Cost Fallback</div>
              <div class="title font-weight-bold">
                {{ fallbackCostPackageCount.toLocaleString() }}
              </div>
            </v-col>
          </v-row>

          <div class="caption grey--text mt-2">
            Invalid/missing and future issue dates do not enter the active liability population.
            Packages without their own actual-cost history use the overall historical actual-cost average.
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          <span class="font-weight-medium">Historical Mature Fulfillment</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p class="grey--text text--darken-1 mb-4">
            Uses non-void MPS records whose normal 1096-day fulfillment window has ended.
            This fulfillment rate is now used for the main reserve calculation.
          </p>

          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Mature Issued</div>
              <div class="title font-weight-bold">
                {{ historicalMatureIssuedCount.toLocaleString() }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Mature Booked</div>
              <div class="title font-weight-bold">
                {{ historicalMatureBookedCount.toLocaleString() }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Mature Fulfillment Rate</div>
              <div class="title font-weight-bold">
                {{ formatPercent(historicalMatureFulfillmentRate) }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Historical Avg Actual Cost</div>
              <div class="title font-weight-bold">
                {{ formatCurrency(historicalMatureAverageActualCost) }}
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <div class="subtitle-2 font-weight-bold mb-2">Historical Revenue / Cost</div>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Original Sales</div>
              <div class="title font-weight-bold">
                {{ formatCurrency(selectedHistoricalRevenueStats.sales) }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Registration Upgrades</div>
              <div class="title font-weight-bold">
                {{ formatCurrency(selectedHistoricalRevenueStats.upgrades) }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Total Revenue</div>
              <div class="title font-weight-bold">
                {{ formatCurrency(selectedHistoricalRevenueStats.revenue) }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Fulfillment Cost %</div>
              <div class="title font-weight-bold">
                {{ formatPercent(selectedHistoricalCostToRevenueRate) }}
              </div>
            </v-col>
          </v-row>

          <div v-if="historicalMatureRangeLabel" class="caption grey--text mt-3">
            Issue-date range: {{ historicalMatureRangeLabel }}. Lifecycle prediction uses traveler
            Date Registered when available. Valid extensions are not yet included.
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          <span class="font-weight-medium">Outstanding Fulfillment Likelihood</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p class="grey--text text--darken-1 mb-4">
            For each currently unfulfilled package, this compares its exact age in days against
            mature historical packages that were still unregistered at the same age. The percentage
            is the share of those historical packages that later registered before expiration.
          </p>

          <v-row class="mb-3">
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Unfulfilled Analyzed</div>
              <div class="title font-weight-bold">
                {{ selectedLifecycleOutstandingCount.toLocaleString() }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Avg Remaining Likelihood</div>
              <div class="title font-weight-bold">
                {{ formatPercent(selectedAverageRemainingLikelihood) }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Projected Future Fulfillments</div>
              <div class="title font-weight-bold">
                {{ formatDecimal(selectedProjectedFutureFulfillments) }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="caption grey--text">Age-Based Expected Liability</div>
              <div class="title font-weight-bold">
                {{ formatCurrency(selectedAgeBasedExpectedLiability) }}
              </div>
            </v-col>
          </v-row>

          <v-data-table
            :headers="lifecycleHeaders"
            :items="outstandingLifecycleRows"
            :items-per-page="15"
            dense
            class="elevation-0"
            sort-by="remainingLikelihood"
            sort-desc
          >
            <template v-slot:item.avgAgeDays="{ item }">
              {{ Math.round(item.avgAgeDays).toLocaleString() }} days
            </template>
            <template v-slot:item.remainingLikelihood="{ item }">
              <span>{{ formatPercent(item.remainingLikelihood) }}</span>
              <v-chip
                v-if="item.fallbackCount > 0"
                x-small
                outlined
                class="ml-1"
              >
                {{ item.fallbackCount }} overall fallback
              </v-chip>
            </template>
            <template v-slot:item.projectedFutureFulfillments="{ item }">
              {{ formatDecimal(item.projectedFutureFulfillments) }}
            </template>
            <template v-slot:item.projectedNotFulfilled="{ item }">
              {{ formatDecimal(item.projectedNotFulfilled) }}
            </template>
            <template v-slot:item.ageBasedExpectedLiability="{ item }">
              <strong>{{ formatCurrency(item.ageBasedExpectedLiability) }}</strong>
            </template>
          </v-data-table>

          <div class="caption grey--text mt-3">
            Package-specific history is used when historical packages of that type were still at risk
            at the same age. Otherwise the overall mature historical curve is used. This is a
            comparison model and does not replace the main historical reserve calculation.
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card outlined class="mt-4">
      <v-card-title>Current Calculation Logic</v-card-title>
      <v-card-text>
        <div class="mb-2">
          <strong>Active window:</strong>
          MPS records from Issue Date through Issue Date + 1096 days.
        </div>
        <div class="mb-2">
          <strong>Fulfilled / booked:</strong>
          A matching user account booked/confirmed flag, a valid user account Date Confirmed,
          an MPS booked flag, or a valid MPS Date Booked indicates the package has been booked.
        </div>
        <div class="mb-2">
          <strong>Voided records:</strong>
          MPS records marked VOID / VOIDED in Prior Reservations, Date Booked, or Confirmation Number
          are excluded from report calculations.
        </div>
        <div class="mb-2">
          <strong>Actual fulfillment cost:</strong>
          Total Room Cost + Tours &amp; Inclusions Total Cost. Total Package Cost is used as a fallback.
          Package projections use all available actual cost history for that package. If a package has
          no actual cost history, the overall historical mature average cost is used.
        </div>
        <div class="mb-2">
          <strong>Potential liability:</strong>
          Outstanding packages x expected fulfillment cost. Year 3 uses
          max(Avg Actual Cost - $599, $0).
        </div>
        <div class="mb-2">
          <strong>Reserve requirement:</strong>
          Potential liability x the historical mature fulfillment rate from packages whose full
          1096-day window has ended.
        </div>
        <div class="mb-2">
          <strong>Package reserve comparison:</strong>
          Uses that package's mature historical fulfillment rate when available. Packages with no
          mature history fall back to the overall historical mature rate.
        </div>
        <div class="mb-2">
          <strong>Sales + upgrades:</strong>
          Total Sales uses MPS Price, with Net Sale + Commission Paid as a fallback when Price is blank.
          Registration Upgrades uses the MPS Upgrade field, which is populated from paid booking
          surcharges/upgrades. Historical fulfillment cost % is mature actual fulfillment cost divided
          by mature sales + upgrades.
        </div>
        <div>
          <strong>Age-based likelihood:</strong>
          For an unfulfilled package at its current age, the model looks at mature historical packages
          that were still unregistered at that same age and calculates the percentage that later
          registered before the 1096-day expiration.
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import { portalsApp } from "@/firebase";

export default {
  name: "Fulfillment",

  data() {
    return {
      loading: false,
      search: "",
      packageFilter: null,
      bucketFilter: "all",
      rows: [],
      voidedExcludedCount: 0,
      invalidIssueDateCount: 0,
      missingIssueDateCount: 0,
      futureIssueDateCount: 0,
      headers: [
        { text: "Package", value: "packageCode" },
        { text: "Issued", value: "selectedIssued" },
        { text: "Booked", value: "selectedBooked" },
        { text: "Unfulfilled", value: "selectedOutstanding" },
        { text: "Current Booked Rate", value: "selectedPackageBookedRate" },
        { text: "Historical Rate", value: "historicalPackageRate" },
        { text: "Avg Fulfillment Cost", value: "avgActualCost" },
        { text: "Total Sales", value: "selectedSales" },
        { text: "Upgrade Income", value: "selectedUpgradeIncome" },
        { text: "Total Revenue", value: "selectedSalesPlusUpgrades" },
        { text: "Hist. Fulfillment Cost %", value: "historicalCostToRevenueRate" },
        { text: "Potential Liability", value: "selectedPotential" },
        { text: "Reserve - Historical Overall", value: "selectedReserveOverall" },
        { text: "Reserve - Historical Package", value: "selectedReservePackage" },
      ],
      lifecycleHeaders: [
        { text: "Package", value: "packageCode" },
        { text: "Unfulfilled", value: "outstanding" },
        { text: "Avg Age", value: "avgAgeDays" },
        { text: "Likely to Fulfill", value: "remainingLikelihood" },
        { text: "Projected Fulfill", value: "projectedFutureFulfillments" },
        { text: "Projected Not Fulfill", value: "projectedNotFulfilled" },
        { text: "Avg Hist. Sample", value: "avgHistoricalSample" },
        { text: "Age-Based Expected Liability", value: "ageBasedExpectedLiability" },
      ],
    };
  },

  computed: {
    rollingRows() {
      return this.rows.filter((row) => row.inRollingWindow);
    },

    historicalMatureRows() {
      return this.rows.filter((row) => row.historicalMature);
    },

    historicalMatureIssuedCount() {
      return this.historicalMatureRows.length;
    },

    historicalMatureBookedCount() {
      return this.historicalMatureRows.filter((row) => row.booked).length;
    },

    historicalMatureFulfillmentRate() {
      return this.historicalMatureIssuedCount
        ? this.historicalMatureBookedCount / this.historicalMatureIssuedCount
        : 0;
    },

    historicalMatureAverageActualCost() {
      var rows = this.historicalMatureRows.filter(
        (row) => row.booked && row.actualCost > 0,
      );

      if (!rows.length) return this.allHistoryAverageActualCost;

      return rows.reduce((sum, row) => sum + row.actualCost, 0) / rows.length;
    },

    allHistoryAverageActualCost() {
      var rows = this.rows.filter((row) => row.booked && row.actualCost > 0);
      if (!rows.length) return 0;
      return rows.reduce((sum, row) => sum + row.actualCost, 0) / rows.length;
    },

    historicalMatureRangeLabel() {
      if (!this.historicalMatureRows.length) return "";

      var dates = this.historicalMatureRows
        .map((row) => this.parseDate(row.issueDate))
        .filter((date) => !!date)
        .sort((a, b) => a.valueOf() - b.valueOf());

      if (!dates.length) return "";

      return (
        dates[0].format("MM/DD/YYYY") +
        " - " +
        dates[dates.length - 1].format("MM/DD/YYYY")
      );
    },

    selectedHistoricalRevenueStats() {
      var rows = this.historicalMatureRows;
      if (this.packageFilter) {
        rows = rows.filter((row) => row.packageCode === this.packageFilter);
      }

      return rows.reduce(
        (totals, row) => {
          totals.sales += row.saleAmount;
          totals.upgrades += row.upgradeIncome;
          if (row.booked && row.actualCost > 0) totals.actualCost += row.actualCost;
          totals.revenue = totals.sales + totals.upgrades;
          return totals;
        },
        { sales: 0, upgrades: 0, revenue: 0, actualCost: 0 },
      );
    },

    selectedHistoricalCostToRevenueRate() {
      var stats = this.selectedHistoricalRevenueStats;
      return stats.revenue ? stats.actualCost / stats.revenue : 0;
    },

    overallHistoricalRevenueStats() {
      return this.historicalMatureRows.reduce(
        (totals, row) => {
          totals.sales += row.saleAmount;
          totals.upgrades += row.upgradeIncome;
          if (row.booked && row.actualCost > 0) totals.actualCost += row.actualCost;
          totals.revenue = totals.sales + totals.upgrades;
          return totals;
        },
        { sales: 0, upgrades: 0, revenue: 0, actualCost: 0 },
      );
    },

    overallHistoricalCostToRevenueRate() {
      var stats = this.overallHistoricalRevenueStats;
      return stats.revenue ? stats.actualCost / stats.revenue : 0;
    },

    historicalPackageStats() {
      var grouped = {};

      this.historicalMatureRows.forEach((row) => {
        var code = row.packageCode || "UNKNOWN";
        if (!grouped[code]) {
          grouped[code] = {
            issued: 0,
            booked: 0,
            sales: 0,
            upgrades: 0,
            actualCost: 0,
          };
        }

        var group = grouped[code];
        group.issued += 1;
        if (row.booked) group.booked += 1;
        group.sales += row.saleAmount;
        group.upgrades += row.upgradeIncome;
        if (row.booked && row.actualCost > 0) group.actualCost += row.actualCost;
      });

      Object.keys(grouped).forEach((code) => {
        var group = grouped[code];
        group.rate = group.issued ? group.booked / group.issued : 0;
        group.revenue = group.sales + group.upgrades;
        group.costToRevenueRate = group.revenue
          ? group.actualCost / group.revenue
          : 0;
      });

      return grouped;
    },

    historicalLifecycleOverall() {
      return this.buildLifecycleCurve(this.historicalMatureRows);
    },

    historicalLifecycleByPackage() {
      var grouped = {};

      this.historicalMatureRows.forEach((row) => {
        var code = row.packageCode || "UNKNOWN";
        if (!grouped[code]) grouped[code] = [];
        grouped[code].push(row);
      });

      var curves = {};
      Object.keys(grouped).forEach((code) => {
        curves[code] = this.buildLifecycleCurve(grouped[code]);
      });

      return curves;
    },

    selectedOutstandingRawRows() {
      return this.selectedRawRows.filter((row) => row.unfulfilled);
    },

    outstandingLifecycleRows() {
      var grouped = {};
      var overallCostFallback =
        this.historicalMatureAverageActualCost || this.allHistoryAverageActualCost;

      this.selectedOutstandingRawRows.forEach((row) => {
        var code = row.packageCode || "UNKNOWN";
        var prediction = this.getRemainingFulfillmentPrediction(row);
        var costHistory = this.costHistoryByPackage[code];
        var avgCost = costHistory && costHistory.count
          ? costHistory.average
          : overallCostFallback;
        var expectedCost = row.bucket === "year3"
          ? Math.max(avgCost - 599, 0)
          : avgCost;

        if (!grouped[code]) {
          grouped[code] = {
            packageCode: code,
            outstanding: 0,
            ageTotal: 0,
            likelihoodTotal: 0,
            projectedFutureFulfillments: 0,
            sampleTotal: 0,
            fallbackCount: 0,
            ageBasedExpectedLiability: 0,
          };
        }

        var group = grouped[code];
        group.outstanding += 1;
        group.ageTotal += row.ageDays;
        group.likelihoodTotal += prediction.rate;
        group.projectedFutureFulfillments += prediction.rate;
        group.sampleTotal += prediction.sample;
        if (prediction.source === "overall") group.fallbackCount += 1;
        group.ageBasedExpectedLiability += expectedCost * prediction.rate;
      });

      return Object.keys(grouped)
        .map((code) => {
          var group = grouped[code];
          group.avgAgeDays = group.outstanding
            ? group.ageTotal / group.outstanding
            : 0;
          group.remainingLikelihood = group.outstanding
            ? group.likelihoodTotal / group.outstanding
            : 0;
          group.projectedNotFulfilled = Math.max(
            group.outstanding - group.projectedFutureFulfillments,
            0,
          );
          group.avgHistoricalSample = group.outstanding
            ? Math.round(group.sampleTotal / group.outstanding)
            : 0;
          return group;
        })
        .sort((a, b) => b.remainingLikelihood - a.remainingLikelihood);
    },

    selectedLifecycleOutstandingCount() {
      return this.selectedOutstandingRawRows.length;
    },

    selectedAverageRemainingLikelihood() {
      if (!this.selectedLifecycleOutstandingCount) return 0;
      return (
        this.outstandingLifecycleRows.reduce(
          (sum, row) => sum + row.projectedFutureFulfillments,
          0,
        ) / this.selectedLifecycleOutstandingCount
      );
    },

    selectedProjectedFutureFulfillments() {
      return this.outstandingLifecycleRows.reduce(
        (sum, row) => sum + row.projectedFutureFulfillments,
        0,
      );
    },

    selectedAgeBasedExpectedLiability() {
      return this.outstandingLifecycleRows.reduce(
        (sum, row) => sum + row.ageBasedExpectedLiability,
        0,
      );
    },

    costHistoryByPackage() {
      var grouped = {};

      this.rows.forEach((row) => {
        if (!row.booked || row.actualCost <= 0) return;

        var code = row.packageCode || "UNKNOWN";
        if (!grouped[code]) {
          grouped[code] = {
            total: 0,
            count: 0,
          };
        }

        grouped[code].total += row.actualCost;
        grouped[code].count += 1;
      });

      Object.keys(grouped).forEach((code) => {
        grouped[code].average = grouped[code].count
          ? grouped[code].total / grouped[code].count
          : 0;
      });

      return grouped;
    },

    packageRows() {
      var grouped = {};
      var overallHistoricalRate = this.historicalMatureFulfillmentRate;
      var overallCostFallback =
        this.historicalMatureAverageActualCost || this.allHistoryAverageActualCost;

      this.rollingRows.forEach((row) => {
        var code = row.packageCode || "UNKNOWN";

        if (!grouped[code]) {
          grouped[code] = {
            packageCode: code,
            issued: 0,
            booked: 0,
            year1Issued: 0,
            year1Booked: 0,
            year1Outstanding: 0,
            year2Issued: 0,
            year2Booked: 0,
            year2Outstanding: 0,
            year3Issued: 0,
            year3Booked: 0,
            year3Outstanding: 0,
            year1Sales: 0,
            year2Sales: 0,
            year3Sales: 0,
            year1UpgradeIncome: 0,
            year2UpgradeIncome: 0,
            year3UpgradeIncome: 0,
            year1Potential: 0,
            year2Potential: 0,
            year3Potential: 0,
            potentialLiability: 0,
          };
        }

        var group = grouped[code];
        group.issued += 1;
        if (row.booked) group.booked += 1;

        if (row.bucket === "year1") {
          group.year1Issued += 1;
          if (row.booked) group.year1Booked += 1;
          if (row.unfulfilled) group.year1Outstanding += 1;
          group.year1Sales += row.saleAmount;
          group.year1UpgradeIncome += row.upgradeIncome;
        }

        if (row.bucket === "year2") {
          group.year2Issued += 1;
          if (row.booked) group.year2Booked += 1;
          if (row.unfulfilled) group.year2Outstanding += 1;
          group.year2Sales += row.saleAmount;
          group.year2UpgradeIncome += row.upgradeIncome;
        }

        if (row.bucket === "year3") {
          group.year3Issued += 1;
          if (row.booked) group.year3Booked += 1;
          if (row.unfulfilled) group.year3Outstanding += 1;
          group.year3Sales += row.saleAmount;
          group.year3UpgradeIncome += row.upgradeIncome;
        }
      });

      return Object.keys(grouped)
        .map((code) => {
          var group = grouped[code];
          var costHistory = this.costHistoryByPackage[code];
          var historicalStats = this.historicalPackageStats[code];

          group.currentBookedRate = group.issued ? group.booked / group.issued : 0;
          group.avgActualCost = costHistory && costHistory.count
            ? costHistory.average
            : overallCostFallback;
          group.costUsesOverallFallback = !(costHistory && costHistory.count);

          group.historicalMatureIssued = historicalStats ? historicalStats.issued : 0;
          group.historicalMatureBooked = historicalStats ? historicalStats.booked : 0;
          group.historicalPackageRate = historicalStats ? historicalStats.rate : null;
          group.appliedHistoricalPackageRate = historicalStats
            ? historicalStats.rate
            : overallHistoricalRate;
          group.historicalCostToRevenueRate = historicalStats
            ? historicalStats.costToRevenueRate
            : null;
          group.appliedHistoricalCostToRevenueRate = historicalStats
            ? historicalStats.costToRevenueRate
            : this.overallHistoricalCostToRevenueRate;

          group.year1Potential = group.year1Outstanding * group.avgActualCost;
          group.year2Potential = group.year2Outstanding * group.avgActualCost;

          var year3NetCost = Math.max(group.avgActualCost - 599, 0);
          group.year3Potential = group.year3Outstanding * year3NetCost;
          group.potentialLiability =
            group.year1Potential + group.year2Potential + group.year3Potential;

          return group;
        })
        .sort(
          (a, b) =>
            b.potentialLiability * overallHistoricalRate -
            a.potentialLiability * overallHistoricalRate,
        );
    },

    packageOptions() {
      return this.packageRows
        .map((row) => row.packageCode)
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort();
    },

    filteredPackageRows() {
      if (!this.packageFilter) return this.packageRows;
      return this.packageRows.filter((row) => row.packageCode === this.packageFilter);
    },

    rawCurrentBookedRate() {
      var issued = this.rollingRows.length;
      var booked = this.rollingRows.filter((row) => row.booked).length;
      return issued ? booked / issued : 0;
    },

    selectedRawRows() {
      var rows = this.packageFilteredRawRows;
      if (this.bucketFilter !== "all") {
        rows = rows.filter((row) => row.bucket === this.bucketFilter);
      }
      return rows;
    },

    selectedIssuedCount() {
      return this.selectedRawRows.length;
    },

    selectedBookedCount() {
      return this.selectedRawRows.filter((row) => row.booked).length;
    },

    selectedUnfulfilledCount() {
      return this.selectedPackageRows.reduce(
        (total, row) => total + row.selectedOutstanding,
        0,
      );
    },

    selectedCurrentBookedRate() {
      return this.selectedIssuedCount
        ? this.selectedBookedCount / this.selectedIssuedCount
        : 0;
    },

    selectedPotentialLiability() {
      return this.selectedPackageRows.reduce(
        (total, row) => total + row.selectedPotential,
        0,
      );
    },

    selectedReserveRequirement() {
      return this.selectedPotentialLiability * this.historicalMatureFulfillmentRate;
    },

    selectedSalesTotal() {
      return this.selectedRawRows.reduce((sum, row) => sum + row.saleAmount, 0);
    },

    selectedUpgradeIncome() {
      return this.selectedRawRows.reduce((sum, row) => sum + row.upgradeIncome, 0);
    },

    selectedSalesPlusUpgrades() {
      return this.selectedSalesTotal + this.selectedUpgradeIncome;
    },

    selectedPackageRows() {
      var overallHistoricalRate = this.historicalMatureFulfillmentRate;

      return this.filteredPackageRows.map((row) => {
        var selectedIssued = row.issued;
        var selectedBooked = row.booked;
        var selectedOutstanding =
          row.year1Outstanding + row.year2Outstanding + row.year3Outstanding;
        var selectedPotential = row.potentialLiability;
        var selectedSales = row.year1Sales + row.year2Sales + row.year3Sales;
        var selectedUpgradeIncome =
          row.year1UpgradeIncome + row.year2UpgradeIncome + row.year3UpgradeIncome;

        if (this.bucketFilter === "year1") {
          selectedIssued = row.year1Issued;
          selectedBooked = row.year1Booked;
          selectedOutstanding = row.year1Outstanding;
          selectedPotential = row.year1Potential;
          selectedSales = row.year1Sales;
          selectedUpgradeIncome = row.year1UpgradeIncome;
        } else if (this.bucketFilter === "year2") {
          selectedIssued = row.year2Issued;
          selectedBooked = row.year2Booked;
          selectedOutstanding = row.year2Outstanding;
          selectedPotential = row.year2Potential;
          selectedSales = row.year2Sales;
          selectedUpgradeIncome = row.year2UpgradeIncome;
        } else if (this.bucketFilter === "year3") {
          selectedIssued = row.year3Issued;
          selectedBooked = row.year3Booked;
          selectedOutstanding = row.year3Outstanding;
          selectedPotential = row.year3Potential;
          selectedSales = row.year3Sales;
          selectedUpgradeIncome = row.year3UpgradeIncome;
        }

        var selectedPackageBookedRate = selectedIssued
          ? selectedBooked / selectedIssued
          : 0;

        return Object.assign({}, row, {
          selectedIssued: selectedIssued,
          selectedBooked: selectedBooked,
          selectedOutstanding: selectedOutstanding,
          selectedPackageBookedRate: selectedPackageBookedRate,
          selectedSales: selectedSales,
          selectedUpgradeIncome: selectedUpgradeIncome,
          selectedSalesPlusUpgrades: selectedSales + selectedUpgradeIncome,
          selectedPotential: selectedPotential,
          selectedReserveOverall: selectedPotential * overallHistoricalRate,
          selectedReservePackage:
            selectedPotential * row.appliedHistoricalPackageRate,
        });
      });
    },

    packageFilteredRawRows() {
      if (!this.packageFilter) return this.rollingRows;
      return this.rollingRows.filter((row) => row.packageCode === this.packageFilter);
    },

    bucketSummaries() {
      var keys = [
        { key: "year1", label: "Year 1 (0-12 Months)" },
        { key: "year2", label: "Year 2 (12-24 Months)" },
        { key: "year3", label: "Year 3 (24-36 Months)" },
      ];

      return keys.map((bucket) => {
        var count = 0;
        var potential = 0;

        this.filteredPackageRows.forEach((row) => {
          if (bucket.key === "year1") {
            count += row.year1Outstanding;
            potential += row.year1Potential;
          }
          if (bucket.key === "year2") {
            count += row.year2Outstanding;
            potential += row.year2Potential;
          }
          if (bucket.key === "year3") {
            count += row.year3Outstanding;
            potential += row.year3Potential;
          }
        });

        return {
          key: bucket.key,
          label: bucket.label,
          count: count,
          potentialLiability: potential,
          reserveRequirement: potential * this.historicalMatureFulfillmentRate,
        };
      });
    },

    fallbackCostPackageCount() {
      return this.packageRows.filter((row) => row.costUsesOverallFallback).length;
    },

    currentBucketLabel() {
      if (this.bucketFilter === "year1") return "Year 1";
      if (this.bucketFilter === "year2") return "Year 2";
      if (this.bucketFilter === "year3") return "Year 3";
      return "Rolling 3 years";
    },
  },

  mounted() {
    this.loadData();
  },

  methods: {
    isVoidedMpsRecord(data) {
      var voidFields = [
        data.priorReservations,
        data.dateBooked,
        data.confirmationNumber,
      ];

      return voidFields.some((value) => {
        if (value === null || value === undefined) return false;
        return /\bVOID(?:ED)?\b/i.test(String(value).trim());
      });
    },

    async loadData() {
      this.loading = true;

      try {
        var results = await Promise.all([
          portalsApp.firestore().collection("mps").get(),
          portalsApp.firestore().collection("userAccounts").get(),
        ]);

        var mpsSnapshot = results[0];
        var userSnapshot = results[1];
        var userByPuid = {};

        userSnapshot.forEach((doc) => {
          var data = doc.data() || {};
          var key = this.normalizeKey(data.packageID);
          if (!key) return;

          var existing = userByPuid[key];
          var incomingTransferred = data.transferred === true;
          var existingTransferred = existing && existing.transferred === true;

          if (!existing || (existingTransferred && !incomingTransferred)) {
            userByPuid[key] = data;
          }
        });

        var now = moment().startOf("day");
        var rows = [];
        var voidedExcludedCount = 0;
        var invalidIssueDateCount = 0;
        var missingIssueDateCount = 0;
        var futureIssueDateCount = 0;

        mpsSnapshot.forEach((doc) => {
          var data = doc.data() || {};
          var isVoided = this.isVoidedMpsRecord(data);
          var rawIssueDate =
            data.issueDate === undefined || data.issueDate === null
              ? ""
              : String(data.issueDate).trim();
          var issueDate = this.parseDate(data.issueDate);

          if (!issueDate) {
            if (!isVoided) {
              if (rawIssueDate) invalidIssueDateCount += 1;
              else missingIssueDateCount += 1;
            }
            return;
          }

          var expirationDate = issueDate.clone().add(1096, "days");
          var inRollingWindow =
            issueDate.isSameOrBefore(now, "day") &&
            now.isSameOrBefore(expirationDate, "day");

          if (isVoided) {
            if (inRollingWindow) voidedExcludedCount += 1;
            return;
          }

          if (issueDate.isAfter(now, "day")) {
            futureIssueDateCount += 1;
            return;
          }

          var historicalMature = now.isAfter(expirationDate, "day");
          var packageCode = String(data.packageCode || "UNKNOWN")
            .trim()
            .toUpperCase();
          var puidKey = this.normalizeKey(data.puid);
          var userAccount = puidKey ? userByPuid[puidKey] : null;

          var booked = !!(
            (userAccount &&
              (userAccount.isBooked === true ||
                userAccount.isConfirmed === true ||
                this.parseDate(userAccount.dateConfirmed))) ||
            data.booked === true ||
            this.parseDate(data.dateBooked)
          );

          var registrationDate = null;
          if (userAccount) {
            registrationDate = this.parseDate(userAccount.dateRegistered);
          }
          if (!registrationDate) {
            registrationDate = this.parseDate(data.registrationDate);
          }

          var registrationBucket = this.getRegistrationBucket(
            issueDate,
            registrationDate,
            expirationDate,
          );
          var registrationDays = null;
          if (registrationDate && registrationBucket !== "none") {
            registrationDays = registrationDate.diff(issueDate, "days");
          }
          var ageDays = Math.max(0, Math.min(now.diff(issueDate, "days"), 1096));

          var yearTwoStart = issueDate.clone().add(1, "year");
          var yearThreeStart = issueDate.clone().add(2, "years");
          var bucket = "other";

          if (inRollingWindow && now.isBefore(yearTwoStart, "day")) {
            bucket = "year1";
          } else if (
            inRollingWindow &&
            now.isSameOrAfter(yearTwoStart, "day") &&
            now.isBefore(yearThreeStart, "day")
          ) {
            bucket = "year2";
          } else if (
            inRollingWindow &&
            now.isSameOrAfter(yearThreeStart, "day")
          ) {
            bucket = "year3";
          }

          var totalRoomCost = this.toNumber(data.totalRoomCost);
          var toursCost = this.toNumber(data.toursAndInclusionsTotalCost);
          var actualCost = totalRoomCost + toursCost;

          if (actualCost <= 0) {
            actualCost = this.toNumber(data.totalPackageCost);
          }

          var saleAmount = this.toNumber(data.price);
          if (saleAmount <= 0) {
            saleAmount =
              this.toNumber(data.netSale) + this.toNumber(data.commPaid);
          }

          var upgradeIncome = this.toNumber(data.upgrade);
          var unfulfilled = inRollingWindow && !booked;

          rows.push({
            id: doc.id,
            puid: data.puid || "",
            traveler: data.traveler || "",
            packageCode: packageCode,
            issueDate: issueDate.format("YYYY-MM-DD"),
            expirationDate: expirationDate.format("YYYY-MM-DD"),
            registrationDate: registrationDate
              ? registrationDate.format("YYYY-MM-DD")
              : "",
            registrationBucket: registrationBucket,
            registrationDays: registrationDays,
            ageDays: ageDays,
            bucket: bucket,
            booked: booked,
            unfulfilled: unfulfilled,
            inRollingWindow: inRollingWindow,
            historicalMature: historicalMature,
            actualCost: actualCost,
            saleAmount: saleAmount,
            upgradeIncome: upgradeIncome,
          });
        });

        this.rows = rows;
        this.voidedExcludedCount = voidedExcludedCount;
        this.invalidIssueDateCount = invalidIssueDateCount;
        this.missingIssueDateCount = missingIssueDateCount;
        this.futureIssueDateCount = futureIssueDateCount;
      } catch (error) {
        console.error("Error loading fulfillment report:", error);

        if (this.$toasted) {
          this.$toasted.error("Unable to load fulfillment report.");
        }
        if (this.$toast && this.$toast.error) {
          this.$toast.error("Unable to load fulfillment report.");
        }
      }

      this.loading = false;
    },

    getRegistrationBucket(issueDate, registrationDate, expirationDate) {
      if (!issueDate || !registrationDate) return "none";
      if (registrationDate.isBefore(issueDate, "day")) return "none";

      var year2Start = issueDate.clone().add(1, "year");
      var year3Start = issueDate.clone().add(2, "years");

      if (registrationDate.isBefore(year2Start, "day")) return "year1";
      if (registrationDate.isBefore(year3Start, "day")) return "year2";
      if (registrationDate.isSameOrBefore(expirationDate, "day")) return "year3";
      return "none";
    },

    buildLifecycleCurve(rows) {
      var maxDays = 1096;
      var issued = rows.length;
      var registrationCounts = new Array(maxDays + 1).fill(0);
      var totalRegistered = 0;

      rows.forEach((row) => {
        var days = row.registrationDays;
        if (days === null || days === undefined) return;
        days = Math.max(0, Math.min(Math.round(days), maxDays));
        registrationCounts[days] += 1;
        totalRegistered += 1;
      });

      var curve = new Array(maxDays + 1);
      var registeredThroughAge = 0;

      for (var day = 0; day <= maxDays; day += 1) {
        registeredThroughAge += registrationCounts[day];
        var atRisk = Math.max(issued - registeredThroughAge, 0);
        var futureRegistrations = Math.max(
          totalRegistered - registeredThroughAge,
          0,
        );

        curve[day] = {
          rate: atRisk ? futureRegistrations / atRisk : 0,
          sample: atRisk,
          futureRegistrations: futureRegistrations,
        };
      }

      return {
        issued: issued,
        totalRegistered: totalRegistered,
        curve: curve,
      };
    },

    getRemainingFulfillmentPrediction(row) {
      var age = Math.max(0, Math.min(Math.round(row.ageDays || 0), 1096));
      var packageCurve = this.historicalLifecycleByPackage[row.packageCode];

      if (
        packageCurve &&
        packageCurve.curve[age] &&
        packageCurve.curve[age].sample > 0
      ) {
        return {
          rate: packageCurve.curve[age].rate,
          sample: packageCurve.curve[age].sample,
          source: "package",
        };
      }

      var overall = this.historicalLifecycleOverall.curve[age];
      return {
        rate: overall ? overall.rate : 0,
        sample: overall ? overall.sample : 0,
        source: "overall",
      };
    },

    formatDecimal(value) {
      return this.toNumber(value).toLocaleString("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },

    parseDate(value) {
      if (!value) return null;

      var parsed = moment(
        String(value).trim(),
        [
          "YYYY-MM-DD",
          "MM/DD/YYYY",
          "M/D/YYYY",
          "MM-DD-YYYY",
          "M-D-YYYY",
          moment.ISO_8601,
        ],
        true,
      );

      return parsed.isValid() ? parsed.startOf("day") : null;
    },

    normalizeKey(value) {
      if (value === undefined || value === null) return "";
      return String(value).trim().toLowerCase();
    },

    toNumber(value) {
      if (value === undefined || value === null || value === "") return 0;

      var number = Number(
        typeof value === "string"
          ? value.replace(/[$,]/g, "").trim()
          : value,
      );

      return Number.isFinite(number) ? number : 0;
    },

    formatCurrency(value) {
      return this.toNumber(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });
    },

    formatPercent(value) {
      return (this.toNumber(value) * 100).toFixed(1) + "%";
    },
  },
};
</script>

<style scoped>
.bucket-card {
  cursor: pointer;
}
</style>
